/**
 * "Narrative first": the deputy writes what happened, and this picks out what looks like the offense, the address,
 * the times, the people and the stolen property, so they can add each to the report with one tap. It is plain
 * word-and-pattern matching — it misses things and can be wrong — so nothing is ever added without the deputy
 * confirming it, and every state field is still answered by the deputy.
 */
import { OFFENSES } from './codes.ts';
import type { IncidentReport, PersonRole } from './report.ts';

export type NarrativeHint =
  | { kind: 'offense'; key: string; code: string; label: string; because: string }
  | { kind: 'address'; key: string; line1: string; city: string | null }
  | { kind: 'time'; key: string; label: string; date: string | null; time: string }
  | { kind: 'person'; key: string; name: string; role: PersonRole }
  | { kind: 'property'; key: string; description: string };

const SUFFIX = 'Road|Rd|Street|St|Avenue|Ave|Drive|Dr|Lane|Ln|Court|Ct|Highway|Hwy|Boulevard|Blvd|Way|Circle|Cir|Place|Pl|Parkway|Pkwy|Trail|Trl|Loop|Terrace|Ter|Pike|Run|Path|Cove|Square|Sq';
const ADDRESS = new RegExp(`\\b(\\d{1,6}\\s+(?:[NSEW]\\.?\\s+)?(?:[A-Z][A-Za-z'\\-]*\\.?\\s+){1,4}(?:${SUFFIX})\\b\\.?)(?:,?\\s+([A-Z][a-z]+(?:\\s[A-Z][a-z]+)?)(?=[\\s,.;]|$))?`, 'g');
const NOT_CITY = /^(In|On|At|The|And|Upon|Where|Who|She|He|They|I|Deputy|While|Because|After|Before|When|Then)$/;
const TITLES = /^(Deputy|Dep|Sgt|Sergeant|Officer|Ofc|Cpl|Corporal|Lt|Lieutenant|Capt|Captain|Investigator|Inv|Detective|Det|Trooper|Tpr|Chief|Sheriff|Dr)\.?$/;
const STOP_NAME = /^(On|At|In|The|Upon|I|He|She|They|It|This|That|While|When|After|Before|Chester|County|Sheriff|Office|Street|Road|Highway|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|January|February|March|April|May|June|July|August|September|October|November|December|EMS|Walmart|Samsung)$/;

// Words too common in narratives to say which offense it is on their own.
const GENERIC = new Set(['john', 'missing', 'stole', 'stolen', 'took', 'taken', 'took', 'lost', 'found', 'damage', 'damaged', 'called', 'located']);
const sentences = (t: string) => t.split(/(?<=[.!?])\s+|\n+/).filter((s) => s.trim());
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** 1745 → 17:45, 8:30 pm → 20:30. */
function clock(raw: string): string | null {
  const s = raw.toLowerCase().replace(/\s+/g, '');
  let m = /^(\d{1,2}):(\d{2})(a|p)?\.?m?\.?$/.exec(s);
  if (m) {
    let h = +m[1]!;
    if (m[3] === 'p' && h < 12) h += 12;
    if (m[3] === 'a' && h === 12) h = 0;
    return h < 24 && +m[2]! < 60 ? `${String(h).padStart(2, '0')}:${m[2]}` : null;
  }
  m = /^(\d{1,2})(a|p)\.?m\.?$/.exec(s);
  if (m) {
    let h = +m[1]!;
    if (m[2] === 'p' && h < 12) h += 12;
    if (m[2] === 'a' && h === 12) h = 0;
    return h < 24 ? `${String(h).padStart(2, '0')}:00` : null;
  }
  m = /^(\d{2})(\d{2})(?:hours|hrs|hr)?$/.exec(s);
  if (m && +m[1]! < 24 && +m[2]! < 60) return `${m[1]}:${m[2]}`;
  return null;
}

function isoDate(raw: string, fallbackYear: number): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(raw);
  if (!m) return null;
  const mo = +m[1]!;
  const d = +m[2]!;
  let y = m[3] ? +m[3] : fallbackYear;
  if (y < 100) y += 2000;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function narrativeHints(text: string, now = new Date()): NarrativeHint[] {
  const out: NarrativeHint[] = [];
  const seen = new Set<string>();
  const push = (h: NarrativeHint) => {
    if (seen.has(h.key)) return;
    seen.add(h.key);
    out.push(h);
  };
  if (!text || text.trim().length < 10) return out;
  const low = ` ${norm(text)} `;

  // ── Offenses: an offense's everyday words appearing as a whole phrase ──
  const scored = OFFENSES.filter((o) => o.group !== 'NC' || o.code === 'NC-OVERDOSE')
    .map((o) => {
      const hits = o.words.filter((w) => w.length >= 4 && !GENERIC.has(w) && low.includes(` ${norm(w)} `));
      return { o, hits, score: hits.reduce((a, w) => a + w.length, 0) };
    })
    .filter((x) => x.hits.length)
    .sort((a, b) => b.score - a.score);
  for (const x of scored.slice(0, 4)) push({ kind: 'offense', key: `o:${x.o.code}`, code: x.o.code, label: x.o.plain, because: x.hits[0]! });

  // ── Addresses ──
  for (const m of text.matchAll(ADDRESS)) {
    const line1 = m[1]!.replace(/\s+/g, ' ').replace(/\.$/, '').trim();
    const city = m[2] && !NOT_CITY.test(m[2].split(' ')[0]!) ? m[2] : null;
    push({ kind: 'address', key: `a:${norm(line1)}`, line1, city });
  }

  // ── Times (with the date in the same sentence, if any) ──
  // A time with no date in its sentence takes the narrative's first date.
  const firstDate = /\b(\d{1,2}\/\d{1,2}\/\d{2,4})\b/.exec(text);
  const defDate = firstDate ? isoDate(firstDate[1]!, now.getFullYear()) : null;
  for (const s of sentences(text)) {
    const dm = /\b(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\b/.exec(s);
    const date = (dm ? isoDate(dm[1]!, now.getFullYear()) : null) ?? defDate;
    const re = /\b(?:at|around|about|approximately|approx\.?|by|until|till|before|after|between|and)\s+(\d{1,2}:\d{2}\s*[ap]\.?\s*m\.?|\d{1,2}\s*[ap]\.?m\.?|\d{1,2}:\d{2}|\d{4}(?:\s*(?:hours|hrs))?)\b/gi;
    for (const t of s.matchAll(re)) {
      const c = clock(t[1]!);
      if (!c) continue;
      push({ kind: 'time', key: `t:${date ?? ''}:${c}`, label: `${date ? `${date.slice(5).replace('-', '/')} ` : ''}${c}`, date, time: c });
    }
  }

  // ── People: a name after the words that introduce one ──
  const intro = /\b([Vv]ictim|[Cc]omplainant|[Ss]uspect|[Ww]itness|[Aa]rrestee|[Ss]poke (?:with|to)|[Ii]dentified as|[Ii]nterviewed|[Nn]amed|Mr\.|Ms\.|Mrs\.|Miss)[,:]?\s+((?:[A-Z][a-z'\-]+|[A-Z]\.)(?:\s+(?:[A-Z][a-z'\-]+|[A-Z]\.)){1,3})/g;
  for (const m of text.matchAll(intro)) {
    const words = m[2]!.split(/\s+/);
    while (words.length && (STOP_NAME.test(words.at(-1)!) || TITLES.test(words.at(-1)!))) words.pop();
    if (words.length < 1 || words.some((w) => STOP_NAME.test(w) || TITLES.test(w))) continue;
    const before = text.slice(Math.max(0, m.index! - 12), m.index!);
    if (/\b(Deputy|Dep\.|Sgt\.?|Officer|Cpl\.?|Lt\.?|Inv\.?)\s*$/.test(before)) continue;
    const k = m[1]!.toLowerCase();
    const role: PersonRole = k === 'suspect' || k === 'arrestee' ? (k === 'arrestee' ? 'arrestee' : 'suspect') : k === 'witness' ? 'witness' : k === 'complainant' ? 'complainant' : 'victim';
    // "Ms. Hollis" after "Margaret Hollis" is the same person.
    const name = words.join(' ');
    const last = words.at(-1)!;
    if (words.length === 1 && out.some((h) => h.kind === 'person' && h.name.endsWith(` ${last}`))) continue;
    push({ kind: 'person', key: `p:${norm(name)}`, name, role });
  }

  // ── Property taken ──
  const lists = [
    /\b(?:reported|stated|advised|found|noticed|discovered)\s+(?:that\s+)?(?:(?:her|his|their|the)\s+)?(.{3,140}?)\s+(?:was|were|had been)?\s*(?:missing|stolen|taken|gone)\b/gi,
    /\b(?:stole|took|removed|missing:)\s+(.{3,120}?)(?=\s+(?:from|out of|off)\b|[.;\n]|$)/gi,
  ];
  for (const re of lists)
    for (const m of text.matchAll(re)) {
      for (let item of m[1]!.split(/,\s*(?:and\s+)?|\s+and\s+/)) {
        item = item.replace(/^(?:a|an|the|her|his|their|one|two|three|some|several)\s+/i, '').replace(/\s+(?:was|were)$/i, '').trim();
        if (item.length < 3 || item.length > 60 || /^(from|out|off|in|at|to|by|of)\b/i.test(item) || /^(it|them|that|this|something|nothing|anything|items?|property)$/i.test(item) || /\b(he|she|they|i|we)\b/i.test(item)) continue;
        push({ kind: 'property', key: `pr:${norm(item)}`, description: cap(item) });
      }
    }
  return out;
}

/** Whether a hint is already on the report (so the list can show it as added). */
export function hintApplied(h: NarrativeHint, r: IncidentReport, localTime: (iso: string) => string = (iso) => iso.slice(11, 16)): boolean {
  switch (h.kind) {
    case 'offense':
      return r.offenses.some((o) => o.code === h.code);
    case 'address':
      return norm(r.location.line1) === norm(h.line1);
    case 'time':
      return [r.occurredFrom, r.occurredTo].some((x) => !!x && localTime(x) === h.time);
    case 'person':
      return r.people.some((p) => norm(p.name) === norm(h.name));
    case 'property':
      return r.property.some((p) => norm(p.description) === norm(h.description));
  }
}
